/**
 * Lowercase keywords matched against the segment after `", "` in provider stop names.
 *
 * When the post-comma segment equals one of these values (case-insensitive), the
 * `"City, Place"` pattern is kept as-is instead of being inverted or shortened.
 *
 * @remarks Covers generic place types (station, town hall, school, etc.) in FR/DE.
 */
const STOP_NO_INVERT_KEYWORDS = [
    "gare",
    "place",
    "salle communale",
    "p+r",
    "croisée",
    "plateau",
    "village",
    "mairie",
    "douane",
    "ecole",
    "école",
    "temple",
    "eglise",
    "église",
    "terminal",
    "gare routière",
    "marché",
    "port",
    "poste",
    "centre",
    "cité",
    "cite",
    "bahnhof",
    "universität",
    "post",
    "dorf",
    "schulhaus",
    "kindergarten",
    "grundschule",
    "Gemeindehaus",
    "schule",
    "kirche",
    "zoll",
    "gymnasium",
    "zentrum",
    "station",
];

/**
 * Returns whether a `"City, Stop"` name should be reordered or shortened.
 *
 * @param after Segment after the first `", "` separator (stop or place name).
 * @returns `true` when the segment is not a generic place keyword and formatting may proceed.
 */
const shouldInvert = ( after: string ): boolean => {
    const lower = after.toLowerCase().trim();

    return !STOP_NO_INVERT_KEYWORDS.some( ( kw ) => lower === kw.toLowerCase() );
};

/**
 * Formats a stop name for display by swapping city and stop when appropriate.
 *
 * Provider names often arrive as `"Genève, Pré-fleuri"`. When the post-comma segment
 * is a specific stop (not a generic place type), this returns `"Pré-fleuri, Genève"`.
 * Names without `", "` or matching {@link STOP_NO_INVERT_KEYWORDS} are capitalized only.
 *
 * @param name Raw stop name from GTFS / spatial index / popup payload.
 * @returns Display label with first character uppercased.
 *
 * @example
 * formatStopName( "Genève, Pré-fleuri" ); // "Pré-fleuri, Genève"
 * formatStopName( "Lausanne, Gare" );     // "Lausanne, Gare"
 */
export const formatStopName = ( name: string ): string => {
    const commaIndex = name.indexOf( ", " );

    if ( commaIndex === -1 ) {
        return name.charAt( 0 ).toUpperCase() + name.slice( 1 );
    }

    const before = name.slice( 0, commaIndex );
    const after  = name.slice( commaIndex + 2 );

    if ( !shouldInvert( after ) ) {
        return name.charAt( 0 ).toUpperCase() + name.slice( 1 );
    }

    const result = `${ after }, ${ before }`;

    return result.charAt( 0 ).toUpperCase() + result.slice( 1 );
};

/**
 * Formats a stop name for compact map labels by dropping the city prefix.
 *
 * Same inversion rules as {@link formatStopName}, but when reordering applies the
 * city segment is omitted so only the stop name remains (e.g. `"Pré-fleuri"`).
 * Used by MapLibre stop symbol layers via the GeoJSON `labelShort` property.
 *
 * @param name Raw stop name from GTFS / spatial index.
 * @returns Short display label with first character uppercased.
 *
 * @example
 * formatStopNameShort( "Genève, Pré-fleuri" ); // "Pré-fleuri"
 * formatStopNameShort( "Lausanne, Gare" );     // "Lausanne, Gare"
 */
export const formatStopNameShort = ( name: string ): string => {
    const commaIndex = name.indexOf( ", " );

    if ( commaIndex === -1 ) {
        return name.charAt( 0 ).toUpperCase() + name.slice( 1 );
    }
    const after = name.slice( commaIndex + 2 );

    if ( !shouldInvert( after ) ) {
        return name.charAt( 0 ).toUpperCase() + name.slice( 1 );
    }

    return after.charAt( 0 ).toUpperCase() + after.slice( 1 );
};
